import styles from "./LoginDialog.module.css"
import typo from "../../typography.module.css"
import Dialog from "./Dialog"
import TextInput from "../TextInput"
import Button from "../Button"
import { ReactComponent as AppleLogo } from '../../assets/images/apple.svg';
import { ReactComponent as GoogleLogo } from '../../assets/images/google.svg';
import { useTranslation } from "react-i18next"
import { useContext, useState } from "react"
import { GoogleAuthProvider, OAuthProvider, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth } from "../../firebase";
import MainContext from "../../common/MainContext"
import api from "../../api"
import { useNavigate } from "react-router-dom"

const LoginDialog = ({ open, onClose }) => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const context = useContext(MainContext)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('')
    const [error, setError] = useState(null)
    const [signUp, setSignUp] = useState(false)

    const googleProvider = new GoogleAuthProvider()
    googleProvider.setCustomParameters({
        // Localize the Apple authentication screen in Italian.
        locale: 'it'
    });

    const appleProvider = new OAuthProvider('apple.com')
    appleProvider.addScope('email');
    appleProvider.addScope('name');
    appleProvider.setCustomParameters({
        // Localize the Apple authentication screen in Italian.
        locale: 'it'
    });

    const googleSignIn = async () => {
        try {
            await signInWithPopup(auth, googleProvider)
            let user = await api.get("/user/")
            if (user) {
                context.setUser(user)
            }
        }
        catch (error) {
            console.error("Error Google Sign In: ", error)
        }
    }

    const appleSignIn = async () => {
        try {
            await signInWithPopup(auth, appleProvider)
            let user = await api.get("/user/")
            if (user) {
                context.setUser(user)
            }
        }
        catch (error) {
            console.error("Error Apple Sign In: ", error)
        }
    }

    const signIn = async () => {
        try {
            await signInWithEmailAndPassword(auth, email, password)
            let user = await api.get("/user/")
            if (user) {
                context.setUser(user)
            }
        }
        catch (error) {
            if (error.message.includes('invalid-credential')) {
                setError({ code: -1, message: t('errors.invalidCredentials') })
            }
            else {
                setError({ code: -1, message: error.message })
            }
        }
    }

    const onEmailChange = (value) => {
        setEmail(value)
        if (!validateEmail(value)) {
            setError({ code: -1, message: t('errors.invalidEmail') })
        }
        else {
            setError(null)
        }
    }

    const onPasswordChange = (value) => {
        setPassword(value)
    }

    const onPasswordRepeatChange = (value) => {
        setRepeatPassword(value)
        if (signUp) {
            setError(checkPasswords() ? null : { code: -1, message: t('errors.passwordNotEquals') })
        }
    }

    const checkPasswords = () => {
        return (password.length > 0 && repeatPassword.length > 0) ? password === repeatPassword : true
    }

    const validateEmail = (email) => {
        if (email.length > 0) {
            return email.match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            )
        }
        else {
            return true
        }
    }

    return (
        <Dialog open={open} onClose={onClose}>
            {!signUp &&
                <div className={styles.container}>
                    <div className={[typo.title, styles.title].join(' ')}>{t('login.title')}</div>
                    <div className={[typo.body, styles.body].join(' ')}>{t('login.subtitle')}</div>

                    <div className={styles.formContainer}>
                        <TextInput type="text" placeholder={t('login.email')} onKeyUp={onEmailChange}></TextInput>
                        <TextInput type="password" placeholder={t('login.password')} onKeyUp={onPasswordChange}></TextInput>
                        <div className={styles.resetPassword}>

                            <div className={styles.error}>
                                {error &&
                                    <span className={typo.caption}>
                                        {error.message}
                                    </span>
                                }
                            </div>

                            <Button
                                fullWidth
                                appearance="text"
                                style={{ display: 'flex', justifyContent: 'flex-end' }}
                                onClick={() => { navigate("/login") }}>
                                {t('login.forgotPassword')}
                            </Button>
                        </div>
                    </div>

                    <Button onClick={signIn} fullWidth>LOGIN</Button>

                    <div className={styles.textDivider}>
                        <div className={styles.divider}></div>
                        <div className={styles.caption}>{t('login.or')}</div>
                        <div className={styles.divider}></div>
                    </div>

                    <div className={styles.socialLogin}>
                        <button className={styles.googleSignIn} onClick={googleSignIn}>
                            <GoogleLogo className={styles.googleLogo}></GoogleLogo>
                            {t('login.signInWithGoogle')}
                        </button>
                        <button className={styles.appleSignIn} onClick={appleSignIn}>
                            <AppleLogo className={styles.appleLogo}></AppleLogo>
                            {t('login.signInWithApple')}
                        </button>
                    </div>
                    <div className={styles.spacer}></div>
                    <div className={styles.registerContainer}>
                        {t('login.noAccount')}
                        <Button appearance="text" onClick={() => { setSignUp(true) }}>{t('login.register')}</Button>
                    </div>
                </div>
            }
            {signUp &&
                <div className={styles.container}>
                    <div className={[typo.title, styles.title].join(' ')}>{t('signup.title')}</div>
                    <div className={[typo.body, styles.body].join(' ')}>{t('signup.subtitle')}</div>

                    <div className={styles.formContainer}>
                        <TextInput type="text" placeholder={t('signup.email')} onKeyUp={onEmailChange} autocomplete="off"></TextInput>
                        <TextInput type="password" placeholder={t('signup.password')} onKeyUp={onPasswordChange} autocomplete="new-password"></TextInput>
                        <TextInput type="password" placeholder={t('signup.repeatPassword')} onKeyUp={onPasswordRepeatChange} autocomplete="new-password"></TextInput>

                        <div className={styles.error}>
                            {error &&
                                <span className={typo.caption}>
                                    {error.message}
                                </span>
                            }
                        </div>
                    </div>

                    <Button onClick={signUp} fullWidth>{t('signup.register').toUpperCase()}</Button>

                    <div className={styles.textDivider}>
                        <div className={styles.divider}></div>
                        <div className={styles.caption}>{t('signup.or')}</div>
                        <div className={styles.divider}></div>
                    </div>

                    <div className={styles.socialLogin}>
                        <button className={styles.googleSignIn} onClick={googleSignIn}>
                            <GoogleLogo className={styles.googleLogo}></GoogleLogo>
                            {t('signup.signUpWithGoogle')}
                        </button>
                        <button className={styles.appleSignIn}>
                            <AppleLogo className={styles.appleLogo} onClick={appleSignIn}></AppleLogo>
                            {t('signup.signUpWithApple')}
                        </button>
                    </div>
                    <div className={styles.spacer}></div>
                    <div className={styles.registerContainer}>
                        {t('signup.haveAccount')}
                        <Button appearance="text" onClick={() => { setSignUp(false) }}>{t('signup.login')}</Button>
                    </div>

                </div>
            }
        </Dialog>
    )
}

export default LoginDialog